import debounce from 'lodash/debounce';
import React, { useState, useEffect, useCallback } from 'react';

import styles from './styles.module.css';
import { SearchComponentProps, SearchResult, DebounceOption } from './types';
import Loader from '../Loader';
import CrossIcon from '@/assets/icons/cross.svg?react';
import SearchIcon from '@/assets/icons/search.svg?react';

const ESC_KEY = 27;
const ENTER_KEY = 13;
const UP_KEY = 38;
const DOWN_KEY = 40;

export default function SearchComponent({
  defaultValue = '',
  results = [],
  process,
  label,
  required,
  disabled,
  dropBox,
  placeHolder,
  resetSearchField,
  onSearch,
  onSelect,
  onReset,
  error,
  readOnly
}: SearchComponentProps) {
  const [queryString, setQueryString] = useState(defaultValue);
  const [box, setBox] = useState('qusername');
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const performQuery = useCallback(
    debounce(({ query, box }: DebounceOption) => {
      onSearch(query, box);
    }, 500),
    [onSearch]
  );

  useEffect(() => {
    setQueryString(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    if (resetSearchField) resetSearch();
  }, [resetSearchField]);

  const resetSearch = () => {
    setQueryString('');
    setHighlightIndex(-1);
    onReset?.();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQueryString(value);
    setHighlightIndex(-1);

    if (value.length > 2) {
      performQuery({ query: value, box });
    } else {
      onReset?.();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === ESC_KEY) {
      resetSearch();
    } else if (e.keyCode === ENTER_KEY) {
      e.preventDefault();
      if (highlightIndex >= 0 && results[highlightIndex]) {
        handleSelect(results[highlightIndex]);
      } else {
        validateField();
      }
    } else if (e.keyCode === DOWN_KEY) {
      e.preventDefault();
      setHighlightIndex(prev => (prev < results.length - 1 ? prev + 1 : 0));
    } else if (e.keyCode === UP_KEY) {
      e.preventDefault();
      setHighlightIndex(prev => (prev > 0 ? prev - 1 : results.length - 1));
    }
  };

  const validateField = () => {
    const match = results.find(item => item.name === queryString);
    if (match) {
      onSelect?.(match);
    }
  };

  const handleSelect = (item: SearchResult) => {
    setQueryString(item.name);
    setHighlightIndex(-1);
    onSelect?.(item);
  };

  const handleDropBox = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newBox = e.target.value;
    setBox(newBox);
    if (queryString.length > 2)
      performQuery({ query: queryString, box: newBox });
  };

  const resultLength = results.length > 0;
  const inputStyle = resultLength ? styles.inputList : styles.input;

  const showCross =
    (queryString && !process) || (defaultValue && !process && resultLength);
  const showSearch = !showCross && !process && !resultLength;
  const showLoader = process;

  return (
    <div className={styles.formGroup}>
      {label && (
        <label id={`${label}-label`} className={styles.label}>
          {label} {required && <span className={styles.required}>*</span>}
        </label>
      )}

      {dropBox && (
        <select id={`${label}-select`} onChange={handleDropBox}>
          {dropBox.map(unit => (
            <option id={`${unit.label}-option`} key={unit.value} value={unit.value}>
              {unit.label}
            </option>
          ))}
        </select>
      )}

      {readOnly ? (
        <div id={`${label}-input-read-only`} className={styles.inputReadOnly}>
          {queryString || defaultValue}
        </div>
      ) : (
        <div className={styles.inputWrapper}>
          <div className={styles.inputErrorContainer}>
            <div className={styles.inputContainer}>
              <input
                id={`${label}-input`}
                value={queryString}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onClick={() => queryString}
                onBlur={validateField}
                placeholder={placeHolder}
                disabled={disabled}
                className={`${inputStyle} ${error ? styles.inputError : ''}`}
              />

              <div className={styles.inputIcons}>
                {showLoader && (
                  <span id={`${label}-loader`} className={styles.icon}>
                    <Loader size="small" />
                  </span>
                )}
                {showSearch && (
                  <span id={`${label}-search`} className={styles.icon}>
                    <SearchIcon />
                  </span>
                )}
                {showCross && (
                  <span id={`${label}-cross`} className={styles.icon} onClick={() => resetSearch()}>
                    <CrossIcon />
                  </span>
                )}
              </div>
            </div>
            {error && (
              <span
                id={`${label}-error`}
                className={styles.errorMessage}
                role="alert"
              >
                {error}
              </span>
            )}
          </div>

          {results.length > 0 && (
            <div className={styles.resultStyle}>
              <ul id={`${label}-results`}>
                {results.map((item, index) => (
                  <li
                    id={`${item.name}-item`}
                    key={item.name}
                    onMouseDown={() => handleSelect(item)}
                    className={index === highlightIndex ? 'highlighted' : ''}
                  >
                    {item.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
